/**
 * @param {string} label
 * @param {string} path
 * @param {number} order
 * @param {boolean} measurementField
 */
function field(
  label,
  path,
  order,
  measurementField = false,
  { infractionOpts = { infractable: true, fieldNameAlias: null } } = {}
) {
  infractionOpts.fieldNameAlias = infractionOpts.fieldNameAlias || path
  infractionOpts.infractable = infractionOpts.infractable ?? true

  const isRawField = path.startsWith('raw.')

  return {
    label,
    path,
    order,
    isRawField,
    measurementField,
    infractionOpts
  }
}

export const Delivery = {
  raw: {
    deliveryArrivalDate: field('Arrival Date', 'raw.deliveryArrivalDate', 1),
    deliveryArrivalTime: field('Arrival Time', 'raw.deliveryArrivalTime', 2),
    deliveryExitDate: field('Exit Date', 'raw.deliveryExitDate', 3),
    deliveryExitTime: field('Exit Time', 'raw.deliveryExitTime', 4),
    deliveryId: field('Delivery Number', 'raw.deliveryId', 5),
    deliveryAddress: field('Delivery Address', 'raw.deliveryAddress', 6),
    dispatchAddress: field('Dispatch Address', 'raw.dispatchAddress', 7),
    tradeContractor: field('Trade Contractor', 'raw.tradeContractor', 8),
    supplierName: field('Supplier Name', 'raw.supplierName', 9),
    vehicleRegistration: field('Vehicle Reg', 'raw.vehicleRegistration', 10),
    receiverReferenceNumber: field(
      'Receiver Reference',
      'raw.receiverReferenceNumber',
      11
    ),
    supplierReferenceNumber: field(
      'Supplier Reference',
      'raw.supplierReferenceNumber',
      12
    )
  },
  enriched: {
    deliveryArrivalDate: field(
      'Arrival Date',
      'enriched.deliveryArrivalDate',
      1
    ),
    deliveryExitDate: field('Exit Date', 'enriched.deliveryExitDate', 2),
    deliveryArrivalDateUS: field(
      'Arrival Date',
      'enriched.deliveryArrivalDateUS'
    ),
    deliveryExitDateUS: field('Exit Date', 'enriched.deliveryExitDateUS'),
    deliveryId: field('Delivery Number', 'enriched.deliveryId', 3, false, {
      infractionOpts: {
        fieldNameAlias: 'data.deliveryId'
      }
    }),
    deliveryAddress: field('Delivery Address', 'enriched.deliveryAddress', 4),
    deliveryAddressKnown: field(
      'Known Delivery Address',
      'enriched.deliveryAddressKnown',
      5
    ),
    deliveryAddressEntered: field('', 'enriched.deliveryAddressEntered'),
    deliveryAddressCoordinates: field(
      '',
      'enriched.deliveryAddressCoordinates'
    ),
    givenDispatchAddress: field(
      'Dispatch Address',
      'enriched.givenDispatchAddress',
      6
    ),
    dispatchAddressKnown: field(
      'Known Dispatch Address',
      'enriched.dispatchAddressKnown',
      7
    ),
    dispatchAddressEntered: field('', 'enriched.dispatchAddressEntered'),
    dispatchAddressCoordinates: field(
      '',
      'enriched.dispatchAddressCoordinates'
    ),
    tradeContractor: field('Trade Contractor', 'enriched.tradeContractor', 8),
    supplierName: field('Given Supplier Name', 'enriched.supplierName', 9),
    supplierId: field(
      'Known Supplier Company',
      'enriched.supplierId',
      10,
      false,
      {
        infractionOpts: {
          fieldNameAlias: 'data.knownSupplierName'
        }
      }
    ),
    knownSupplierName: field(
      'Known Supplier Name',
      'enriched.knownSupplierName',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    givenSupplierCertifications: field(
      'Given Supplier Certifications',
      'enriched.givenSupplierCertifications',
      11
    ),
    vehicleRegistration: field(
      'Vehicle Reg',
      'enriched.vehicleRegistration',
      12
    ),
    receiverReferenceNumber: field(
      'Receiver Reference',
      'enriched.receiverReferenceNumber',
      13
    ),
    supplierReferenceNumber: field(
      'Supplier Reference',
      'enriched.supplierReferenceNumber',
      14
    ),
    knownSupplierCertifications: field(
      'Known Supplier Certifications',
      'enriched.knownSupplierCertifications',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    notes: field('Notes', 'enriched.notes', 15, false, {
      infractionOpts: {
        infractable: false
      }
    })
  },
  movementId: field('Delivery Number', 'enriched.deliveryId'),
  rawLineItems: {
    id: field('Id', 'id'),
    name: field('Name', 'name', 1),
    quantity: field('Quantity (count)', 'quantity', 2),
    height: field('Height', 'height', 3),
    width: field('Width', 'width', 4),
    length: field('Length', 'length', 5),
    volume: field('Volume', 'volume', 6),
    weight: field('Weight', 'weight', 7),
    productionDate: field('Produced At Date', 'productionDate', 8),
    productionTime: field('Produced At Time', 'productionTime', 9),
    usedAtDate: field('Used At Date', 'usedAtDate', 10),
    usedAtTime: field('Used At Time', 'usedAtTime', 11),
    certifications: field('Product Certifications', 'certifications', 12),
    // Use extra-high numbers for isDeleted
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  rawLineItemPrefix: 'raw.products',
  lineItems: {
    id: field('Id', 'id'),
    name: field('Name', 'name', 1),
    quantity: field('Quantity (count)', 'quantity', 2),
    height: field('Height', 'height', 3, true),
    width: field('Width', 'width', 4, true),
    length: field('Length', 'length', 5, true),

    heightUoM: field('Height unit', 'heightUoM'),
    widthUoM: field('Width unit', 'widthUoM'),
    lengthUoM: field('Length unit', 'lengthUoM'),

    calculatedVolume: field('Estimated Volume', 'calculatedVolume', 6, true),
    givenVolume: field('Given Volume', 'givenVolume', 7, true),
    givenVolumeUoM: field('Given Volume Units', 'givenVolumeUoM'),
    estimatedWeight: field('Estimated Weight', 'estimatedWeight', 8, true),
    weight: field('Given Weight', 'weight', 9, true),
    weightUoM: field('Given Weight Units', 'weightUoM'),

    productionTime: field('Produced At', 'productionTime', 10),
    usedAtTime: field('Used At', 'usedAtTime', 11),

    givenCertifications: field(
      'Product Certifications',
      'givenCertifications',
      12
    ),
    knownCertifications: field(
      'Known Product Certifications',
      'knownCertifications',
      null,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    referenceProductId: field(
      'Reference Product',
      'referenceProductId',
      12,
      false,
      {
        infractionOpts: {
          fieldNameAlias: 'knownProductName'
        }
      }
    ),
    knownProductName: field(
      'Reference Product Name',
      'knownProductName',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    ),
    productGroups: field('Product Group', 'productGroups', 13, false, {
      infractionOpts: {
        infractable: false
      }
    }),
    isHiredOrRented: field('Hired or Rented', 'isHiredOrRented', 14),

    // Use extra-high numbers for isDeleted and isRemoved so the 'order' key is consistant between Delivery and Waste
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  lineItemPrefix: 'enriched.products',
  lineItemNameField: 'name',
  lineItemDisplayNamePlural: 'Product Items'
}

export const DeliveryImperial = {
  lineItems: {
    id: field('Id', 'id'),
    name: field('Name', 'name', 1, true),
    heightYards: field('Yards', 'heightYards', undefined, true),
    heightFeet: field('Feet', 'heightFeet', undefined, true),
    heightInches: field('Inches', 'heightInches', undefined, true),
    widthYards: field('Yards', 'widthYards', undefined, true),
    widthFeet: field('Feet', 'widthFeet', undefined, true),
    widthInches: field('Inches', 'widthInches', undefined, true),
    lengthYards: field('Yards', 'lengthYards', undefined, true),
    lengthFeet: field('Feet', 'lengthFeet', undefined, true),
    lengthInches: field('Inches', 'lengthInches', undefined, true),
    calculatedVolume: field('Estimated Volume', 'calculatedVolume', 6),
    givenVolume: field('Given Volume', 'givenVolume', 7, true),
    givenVolumeUoM: field('Given Volume Units', 'givenVolumeUoM'),
    estimatedWeight: field('Estimated Weight', 'estimatedWeight', 8),
    weight: field('Given Weight', 'weight', 9, true),
    weightUoM: field('Given Weight Units', 'weightUoM'),
    productionTimeUS: field('Produced At', 'productionTimeUS', 10, true),
    usedAtTimeUS: field('Used At', 'usedAtTimeUS', 11, true)
  }
}

export const Waste = {
  raw: {
    removalDate: field('Collection Arrival Date', 'raw.removalDate', 1),
    removalTime: field('Collection Arrival Time', 'raw.removalTime', 2),
    removalExitDate: field('Collection Exit Date', 'raw.removalExitDate', 3),
    removalExitTime: field('Collection Exit Time', 'raw.removalExitTime', 4),
    consignmentId: field('Consignment Number (WTN)', 'raw.consignmentId', 5),
    subcontractorName: field('Subcontractor', 'raw.subcontractorName', 6),
    originSiteAddress: field('Origin Site Address', 'raw.originSiteAddress', 7),
    classificationCode: field('SIC Code', 'raw.classificationCode', 8),
    vehicleRegistration: field('Vehicle Reg', 'raw.vehicleRegistration', 9),
    carrierName: field('Carrier Name', 'raw.carrierName', 10),
    carrierLicenceId: field('Carrier Licence', 'raw.carrierLicenceId', 11),
    facilityOperatorName: field(
      'Facility Operator',
      'raw.facilityOperatorName',
      12
    ),
    facilitySiteAddress: field(
      'Facility Site Address',
      'raw.facilitySiteAddress',
      13
    ),
    facilityPermitId: field('Facility Permit', 'raw.facilityPermitId', 14),
    facilityPermitPresented: field(
      'Permit Presented',
      'raw.facilityPermitPresented',
      15
    ),
    receiverReferenceNumber: field(
      'Sender Reference',
      'raw.receiverReferenceNumber',
      16
    ),
    supplierReferenceNumber: field(
      'Carrier Reference',
      'raw.supplierReferenceNumber',
      17
    )
  },
  enriched: {
    removalDate: field('Collection Arrival', 'enriched.removalDate', 1),
    removalDateUS: field('Collection Arrival', 'enriched.removalDateUS'),
    removalExitDate: field('Collection Exit', 'enriched.removalExitDate', 2),
    removalExitDateUS: field('Collection Exit', 'enriched.removalExitDateUS'),
    consignmentId: field(
      'Consignment Number (WTN)',
      'enriched.consignmentId',
      3,
      false,
      {
        infractionOpts: {
          fieldNameAlias: 'data.consignmentId'
        }
      }
    ),
    subcontractorName: field('Subcontractor', 'enriched.subcontractorName', 4),
    originSiteAddress: field(
      'Origin Site Address',
      'enriched.originSiteAddress',
      5
    ),
    originSiteAddressKnown: field(
      'Known Origin Site Address',
      'enriched.originSiteAddressKnown',
      6
    ),
    originSiteAddressEntered: field('', 'enriched.originSiteAddressEntered'),
    originSiteAddressCoordinates: field(
      '',
      'enriched.originSiteAddressCoordinates'
    ),
    classificationCode: field('SIC Code', 'enriched.classificationCode', 7),
    vehicleRegistration: field(
      'Vehicle Reg',
      'enriched.vehicleRegistration',
      8
    ),
    carrierName: field('Carrier Name', 'enriched.carrierName', 9),
    carrierLicenceId: field('Carrier Licence', 'enriched.carrierLicenceId', 10),
    facilityOperatorName: field(
      'Facility Operator',
      'enriched.facilityOperatorName',
      11
    ),
    facilitySiteAddress: field(
      'Facility Site Address',
      'enriched.facilitySiteAddress',
      12
    ),
    facilitySiteAddressKnown: field(
      'Known Facility Site Address',
      'enriched.facilitySiteAddressKnown',
      13
    ),
    facilitySiteAddressEntered: field(
      '',
      'enriched.facilitySiteAddressEntered'
    ),
    facilitySiteAddressCoordinates: field(
      '',
      'enriched.facilitySiteAddressCoordinates'
    ),
    facilityPermitId: field('Facility Permit', 'enriched.facilityPermitId', 14),
    facilityPermitPresented: field(
      'Permit Presented',
      'enriched.facilityPermitPresented',
      15
    ),
    receiverReferenceNumber: field(
      'Sender Reference',
      'enriched.receiverReferenceNumber',
      16
    ),
    supplierReferenceNumber: field(
      'Carrier Reference',
      'enriched.supplierReferenceNumber',
      17
    ),
    notes: field('Notes', 'enriched.notes', 18)
  },
  movementId: field('Consignment Number (WTN)', 'enriched.consignmentId'),
  rawLineItems: {
    id: field('Id', 'id'),
    containerType: field('Container Type', 'containerType', 1),
    wasteDescription: field('Waste Description', 'wasteDescription', 2),
    wasteCode: field('Waste Code (EWC)', 'wasteCode', 3),
    volume: field('Volume', 'volume', 4),
    weight: field('Weight', 'weight', 5),
    quantity: field('Quantity', 'quantity', 6),
    // Use extra-high numbers for isDeleted
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  rawLineItemPrefix: 'raw.items',
  lineItems: {
    id: field('Id', 'id'),
    containerType: field('Container Type', 'containerType', 1),
    knownContainerType: field('Known Container Type', 'knownContainerType', 2),
    wasteDescription: field('Waste Description', 'wasteDescription', 3),
    wasteCode: field('Waste Code (EWC)', 'wasteCode', 4),
    volumeEstimated: field('Estimated Volume', 'volumeEstimated', 5, true),
    volume: field('Given Volume', 'volume', 6, true),
    volumeUoM: field('Volume Units', 'volumeUoM'),
    weightEstimated: field('Estimated Weight', 'weightEstimated', 7, true),
    weight: field('Given Weight', 'weight', 8, true),
    weightUoM: field('Given Weight Units', 'weightUoM'),
    quantity: field('Quantity', 'quantity', 9),
    // Use extra-high numbers for isDeleted and isRemoved so the 'order' key is consistant between Delivery and Waste
    isDeleted: field('Deleted', 'isDeleted', 101),
    isRemoved: field('Removed', 'isRemoved', 102)
  },
  lineItemPrefix: 'enriched.items',
  lineItemNameField: 'containerType',
  lineItemDisplayNamePlural: 'Items'
}

// Note: this is used by unit tests and is here so it can leverage _realistic_ infrastructure and be naturally updated whenever data structures change.
export const FakeModule = {
  enriched: {
    field1: field('Field 1', 'enriched.field1', undefined, false, {
      infractionOpts: {
        fieldNameAlias: 'data.field1'
      }
    }),
    field2: field('Field 2', 'enriched.field2', undefined, false, {
      infractionOpts: {
        fieldNameAlias: 'data.field2Name'
      }
    }),
    notInfractable1: field(
      'Not Infractable 1',
      'enriched.notInfractable1',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    )
  },
  movementId: field('Field 1', 'enriched.field1'),
  lineItems: {
    field3: field('Field 3', 'field3'),
    field4: field('Field 4', 'field4', undefined, false, {
      infractionOpts: {
        fieldNameAlias: 'field4Name'
      }
    }),
    notInfractable2: field(
      'Not Infractable 1',
      'enriched.notInfractable1',
      undefined,
      false,
      {
        infractionOpts: {
          infractable: false
        }
      }
    )
  },
  lineItemPrefix: 'enriched.items',
  lineItemNameField: 'field3',
  lineItemDisplayNamePlural: 'enriched Items'
}

const all = {
  Delivery,
  delivery: Delivery,
  DeliveryImperial,
  deliveryImperial: DeliveryImperial,
  Waste,
  waste: Waste,
  FakeModule,
  fakemodule: FakeModule
}

/**
 * @returns {Delivery}
 */
export const getFields = moduleName => {
  return all[moduleName.toLowerCase()]
}
